import { useEffect, useId, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  archiveContact,
  archiveContactAssignment,
  archiveContactMethod,
  assignContact,
  createContact,
  createContactMethod,
  fetchClientContacts,
  SAFE_CONTACT_WRITE_FAILURE,
  updateContact,
  updateContactAssignment,
  updateContactMethod,
  type ClientContact,
  type ContactAssignment,
  type ContactAssignmentDraft,
  type ContactAssignmentRole,
  type ContactDraft,
  type ContactMethod,
  type ContactMethodDraft,
  type ContactMethodType,
  type ClientContactsReadModel,
} from "../data/client-contacts";
import { fetchClientSiteSetup, type ClientRow, type ClientSiteSetup, type ManagedSite } from "../data/clients-sites";
import { clientsForPicker } from "../lib/setup-site-options";
import { useReadModel } from "../data/useReadModel";
import { confirmDiscardIfDirty } from "../components/confirm-discard";
import { GlassDrawer, GlassDrawerFooter } from "../components/GlassDrawer";
import { GlassModal } from "../components/GlassModal";
import { ContactTargetPicker, type ContactTargetValue } from "../components/ContactTargetPicker";
import { Switch } from "../components/Switch";

// Client contact editor (CONSOLE-CONTACT-PROFILES). Contact directory + methods +
// client/site assignments sit in Setup because they are property profile data, not user
// accounts. Reads are explicit allowlists; all writes flow through audited hosted RPCs.

interface ContactSetupModel {
  contacts: ClientContactsReadModel;
  setup: ClientSiteSetup;
}

type Dialog =
  | { kind: "create-contact" }
  | { kind: "edit-contact"; contact: ClientContact }
  | { kind: "archive-contact"; contact: ClientContact }
  | { kind: "create-method"; contact: ClientContact }
  | { kind: "edit-method"; contact: ClientContact; method: ContactMethod }
  | { kind: "archive-method"; contact: ClientContact; method: ContactMethod }
  | { kind: "create-assignment"; contact: ClientContact }
  | { kind: "edit-assignment"; contact: ClientContact; assignment: ContactAssignment }
  | { kind: "archive-assignment"; contact: ClientContact; assignment: ContactAssignment };

function isContactDrawerDialog(dialog: Dialog): boolean {
  return (
    dialog.kind === "create-contact" ||
    dialog.kind === "edit-contact" ||
    dialog.kind === "create-method" ||
    dialog.kind === "edit-method" ||
    dialog.kind === "create-assignment" ||
    dialog.kind === "edit-assignment"
  );
}

function contactDialogAriaLabel(dialog: Dialog): string {
  switch (dialog.kind) {
    case "create-contact":
      return "New contact";
    case "edit-contact":
      return `Edit contact - ${dialog.contact.displayName}`;
    case "create-method":
      return `Add method - ${dialog.contact.displayName}`;
    case "edit-method":
      return `Edit method - ${dialog.contact.displayName}`;
    case "create-assignment":
      return `Add assignment - ${dialog.contact.displayName}`;
    case "edit-assignment":
      return `Edit assignment - ${dialog.contact.displayName}`;
    case "archive-contact":
      return `Archive contact - ${dialog.contact.displayName}`;
    case "archive-method":
      return `Archive method - ${dialog.contact.displayName}`;
    case "archive-assignment":
      return `Archive assignment - ${dialog.contact.displayName}`;
  }
}

function dialogContactId(dialog: Dialog | null): string | null {
  if (dialog === null || dialog.kind === "create-contact") return null;
  return dialog.contact.contactId;
}

const METHOD_LABELS: Record<ContactMethodType, string> = {
  email: "Email",
  mobile: "Mobile",
  office_phone: "Office phone",
  sms: "SMS",
  voice: "Voice",
  other: "Other",
};

const ROLE_LABELS: Record<ContactAssignmentRole, string> = {
  primary: "Primary",
  operations: "Operations",
  billing: "Billing",
  on_site: "On-site",
  emergency: "Emergency",
  after_hours: "After hours",
  facilities_access: "Facilities access",
  supervisor_escalation: "Supervisor escalation",
};

const METHOD_OPTIONS = Object.keys(METHOD_LABELS) as ContactMethodType[];
const ROLE_OPTIONS = Object.keys(ROLE_LABELS) as ContactAssignmentRole[];

export function ClientContactEditor({
  client,
  accountId,
  canManage = false,
  setup: externalSetup,
}: {
  client: SupabaseClient;
  accountId: string;
  /** client_profile.manage - write affordances only exist when true (server still gates). */
  canManage?: boolean;
  /** Shared client/site list from Setup (keeps assignment targets in sync). */
  setup?: ClientSiteSetup | null;
}) {
  const setupKey = externalSetup
    ? `${externalSetup.clients.map((row) => row.clientId).join("|")}::${externalSetup.sites.map((site) => site.siteId).join("|")}`
    : "";
  const { status, data, error, reload } = useReadModel<ContactSetupModel>(
    async () => {
      const contacts = await fetchClientContacts(client, accountId);
      const setup = externalSetup ?? (await fetchClientSiteSetup(client, accountId));
      return { contacts, setup };
    },
    [client, accountId, setupKey],
  );
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [formDirty, setFormDirty] = useState(false);

  const drawerDialogKey =
    dialog !== null && isContactDrawerDialog(dialog)
      ? dialog.kind === "edit-contact"
        ? `edit-contact:${dialog.contact.contactId}`
        : dialog.kind === "edit-method"
          ? `edit-method:${dialog.method.methodId}`
          : dialog.kind === "edit-assignment"
            ? `edit-assignment:${dialog.assignment.assignmentId}`
            : dialog.kind === "create-method"
              ? `create-method:${dialog.contact.contactId}`
              : dialog.kind === "create-assignment"
                ? `create-assignment:${dialog.contact.contactId}`
                : dialog.kind
      : null;

  useEffect(() => {
    setFormDirty(false);
  }, [drawerDialogKey]);

  function clearDrawer(): void {
    setDialog(null);
    setFailure(null);
    setFormDirty(false);
  }

  function requestDrawerClose(): void {
    if (busy) return;
    if (!confirmDiscardIfDirty(formDirty)) return;
    clearDrawer();
  }

  const clientNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const row of data?.setup.clients ?? []) names.set(row.clientId, row.name);
    return names;
  }, [data]);
  const siteNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const site of data?.setup.sites ?? []) names.set(site.siteId, site.name);
    return names;
  }, [data]);

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading client contacts...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load client contacts."}
      </p>
    );
  }

  const contacts = data?.contacts.contacts ?? [];
  const methods = data?.contacts.methods ?? [];
  const assignments = data?.contacts.assignments ?? [];
  const setup = data?.setup ?? { clients: [], sites: [] };
  const activeContactId = dialogContactId(dialog);

  async function run(action: () => Promise<{ ok: boolean; reason?: string }>): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const outcome = await action();
      if (outcome.ok) {
        setDialog(null);
        reload();
      } else {
        setFailure((outcome as { reason: string }).reason);
      }
    } catch {
      setFailure(SAFE_CONTACT_WRITE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="contactedit">
      {canManage ? (
        <div className="tpl__actions">
          <button
            type="button"
            className="minter__confirm-go"
            onClick={() => {
              setFailure(null);
              setDialog({ kind: "create-contact" });
            }}
          >
            New contact
          </button>
        </div>
      ) : null}

      {contacts.length === 0 ? (
        <p className="surface__state" role="status">
          No client contacts yet{canManage ? " - create the first one above" : ""}.
        </p>
      ) : (
        contacts.map((contact) => {
          const contactMethods = methods.filter((method) => method.contactId === contact.contactId);
          const contactAssignments = assignments.filter((assignment) => assignment.contactId === contact.contactId);
          return (
            <section
              key={contact.contactId}
              className={`ckedit__group contactedit__card${
                activeContactId === contact.contactId ? " ckedit__group--editing" : ""
              }`}
              role="region"
              aria-label={`${contact.displayName} contact profile`}
            >
              <div className="ckedit__head">
                <span className="ckedit__route">{contact.displayName}</span>
                {contact.active ? null : <span className="schedule__chip schedule__chip--draft">Archived</span>}
                {contact.organization !== null ? <span className="ckedit__site">{contact.organization}</span> : null}
                {contact.title !== null ? <span className="ckedit__site">{contact.title}</span> : null}
                {canManage && contact.active ? (
                  <span className="ckedit__actions">
                    <button
                      type="button"
                      className="builder__navbtn"
                      onClick={() => {
                        setFailure(null);
                        setDialog({ kind: "edit-contact", contact });
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="builder__cancel"
                      onClick={() => {
                        setFailure(null);
                        setDialog({ kind: "archive-contact", contact });
                      }}
                    >
                      Archive
                    </button>
                  </span>
                ) : null}
              </div>

              <div className="contactedit__columns">
                <div>
                  <div className="contactedit__subhead">
                    <span>Methods</span>
                    {canManage && contact.active ? (
                      <button
                        type="button"
                        className="builder__navbtn"
                        onClick={() => {
                          setFailure(null);
                          setDialog({ kind: "create-method", contact });
                        }}
                      >
                        Add method
                      </button>
                    ) : null}
                  </div>
                  {contactMethods.length === 0 ? (
                    <p className="surface__state">No contact methods yet.</p>
                  ) : (
                    <ul className="ckedit__rows">
                      {contactMethods.map((method) => (
                        <li key={method.methodId} className="ckedit__row">
                          <span className="schedule__chip schedule__chip--credential">
                            {METHOD_LABELS[method.methodType]}
                          </span>
                          <span className="ckedit__name">{method.methodValue}</span>
                          {method.label !== null ? <span className="ckedit__coords">{method.label}</span> : null}
                          {method.alertCapable ? (
                            <span className="schedule__chip schedule__chip--partial">Alert-capable</span>
                          ) : null}
                          {method.active ? null : (
                            <span className="schedule__chip schedule__chip--draft">Archived</span>
                          )}
                          {canManage && method.active && contact.active ? (
                            <span className="ckedit__actions">
                              <button
                                type="button"
                                className="builder__navbtn"
                                onClick={() => {
                                  setFailure(null);
                                  setDialog({ kind: "edit-method", contact, method });
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="builder__cancel"
                                onClick={() => {
                                  setFailure(null);
                                  setDialog({ kind: "archive-method", contact, method });
                                }}
                              >
                                Archive
                              </button>
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <div className="contactedit__subhead">
                    <span>Assignments</span>
                    {canManage && contact.active ? (
                      <button
                        type="button"
                        className="builder__navbtn"
                        onClick={() => {
                          setFailure(null);
                          setDialog({ kind: "create-assignment", contact });
                        }}
                      >
                        Assign
                      </button>
                    ) : null}
                  </div>
                  {contactAssignments.length === 0 ? (
                    <p className="surface__state">No assignments yet.</p>
                  ) : (
                    <ul className="ckedit__rows">
                      {contactAssignments.map((assignment) => (
                        <li key={assignment.assignmentId} className="ckedit__row">
                          <span className="schedule__chip schedule__chip--credential">{ROLE_LABELS[assignment.role]}</span>
                          <span className="ckedit__name">{targetLabel(assignment, clientNames, siteNames)}</span>
                          {assignment.visibleToGuards ? (
                            <span className="schedule__chip schedule__chip--partial">Guard-visible</span>
                          ) : null}
                          {assignment.alertRecipient ? (
                            <span className="schedule__chip schedule__chip--partial">Alert recipient</span>
                          ) : null}
                          {assignment.active ? null : (
                            <span className="schedule__chip schedule__chip--draft">Archived</span>
                          )}
                          {canManage && assignment.active && contact.active ? (
                            <span className="ckedit__actions">
                              <button
                                type="button"
                                className="builder__navbtn"
                                onClick={() => {
                                  setFailure(null);
                                  setDialog({ kind: "edit-assignment", contact, assignment });
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="builder__cancel"
                                onClick={() => {
                                  setFailure(null);
                                  setDialog({ kind: "archive-assignment", contact, assignment });
                                }}
                              >
                                Archive
                              </button>
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>
          );
        })
      )}

      {canManage && dialog !== null && isContactDrawerDialog(dialog) ? (
        <GlassDrawer
          open
          busy={busy}
          dirty={formDirty}
          size={dialog.kind === "create-assignment" || dialog.kind === "edit-assignment" ? "lg" : "md"}
          ariaLabel={contactDialogAriaLabel(dialog)}
          onClose={clearDrawer}
        >
          {dialog.kind === "create-contact" || dialog.kind === "edit-contact" ? (
            <ContactForm
              key={dialog.kind === "edit-contact" ? dialog.contact.contactId : "create-contact"}
              editing={dialog.kind === "edit-contact" ? dialog.contact : null}
              busy={busy}
              failure={failure}
              onDirtyChange={setFormDirty}
              onClose={requestDrawerClose}
              onSubmit={(draft) =>
                void run(() =>
                  dialog.kind === "create-contact"
                    ? createContact(client, accountId, draft)
                    : updateContact(client, accountId, dialog.contact.contactId, draft),
                )
              }
            />
          ) : dialog.kind === "create-method" || dialog.kind === "edit-method" ? (
            <MethodForm
              key={dialog.kind === "edit-method" ? dialog.method.methodId : "create-method"}
              contact={dialog.contact}
              editing={dialog.kind === "edit-method" ? dialog.method : null}
              busy={busy}
              failure={failure}
              onDirtyChange={setFormDirty}
              onClose={requestDrawerClose}
              onSubmit={(draft) =>
                void run(() =>
                  dialog.kind === "create-method"
                    ? createContactMethod(client, accountId, dialog.contact.contactId, draft)
                    : updateContactMethod(client, accountId, dialog.method.methodId, dialog.contact.contactId, draft),
                )
              }
            />
          ) : dialog.kind === "create-assignment" || dialog.kind === "edit-assignment" ? (
            <AssignmentForm
              key={dialog.kind === "edit-assignment" ? dialog.assignment.assignmentId : "create-assignment"}
              contact={dialog.contact}
              editing={dialog.kind === "edit-assignment" ? dialog.assignment : null}
              clients={clientsForPicker(setup.clients)}
              sites={setup.sites.filter((site) => site.active)}
              clientNames={clientNames}
              busy={busy}
              failure={failure}
              onDirtyChange={setFormDirty}
              onClose={requestDrawerClose}
              onSubmit={(draft) =>
                void run(() =>
                  dialog.kind === "create-assignment"
                    ? assignContact(client, accountId, draft)
                    : updateContactAssignment(client, accountId, dialog.assignment.assignmentId, draft),
                )
              }
            />
          ) : null}
        </GlassDrawer>
      ) : null}

      {canManage && dialog !== null && !isContactDrawerDialog(dialog) ? (
        <GlassModal
          open
          busy={busy}
          ariaLabel={contactDialogAriaLabel(dialog)}
          onClose={() => {
            if (!busy) setDialog(null);
          }}
        >
          {dialog.kind === "archive-contact" ? (
            <ConfirmArchive
              title={`Archive contact - ${dialog.contact.displayName}`}
              warning="The contact leaves active pickers and can no longer be edited, but its historical assignments and audit trail stay preserved."
              busy={busy}
              failure={failure}
              onClose={() => setDialog(null)}
              onConfirm={() => void run(() => archiveContact(client, accountId, dialog.contact.contactId))}
            />
          ) : dialog.kind === "archive-method" ? (
            <ConfirmArchive
              title={`Archive method - ${dialog.contact.displayName}`}
              warning="This method stops being available for alerts and guard reference. Existing history stays preserved."
              busy={busy}
              failure={failure}
              onClose={() => setDialog(null)}
              onConfirm={() => void run(() => archiveContactMethod(client, accountId, dialog.method.methodId))}
            />
          ) : dialog.kind === "archive-assignment" ? (
            <ConfirmArchive
              title={`Archive assignment - ${dialog.contact.displayName}`}
              warning="This contact will no longer be tied to that client or site for guard visibility or alert routing. Existing history stays preserved."
              busy={busy}
              failure={failure}
              onClose={() => setDialog(null)}
              onConfirm={() =>
                void run(() => archiveContactAssignment(client, accountId, dialog.assignment.assignmentId))
              }
            />
          ) : null}
        </GlassModal>
      ) : null}
    </div>
  );
}

function targetLabel(
  assignment: ContactAssignment,
  clientNames: ReadonlyMap<string, string>,
  siteNames: ReadonlyMap<string, string>,
): string {
  if (assignment.siteId !== null) return siteNames.get(assignment.siteId) ?? "Site";
  if (assignment.clientId !== null) return clientNames.get(assignment.clientId) ?? "Client";
  return "Unassigned";
}

function cleanOptional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function ContactForm({
  editing,
  busy,
  failure,
  onClose,
  onSubmit,
  onDirtyChange,
}: {
  editing: ClientContact | null;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onSubmit: (draft: ContactDraft) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const initialDisplayName = editing?.displayName ?? "";
  const initialOrganization = editing?.organization ?? "";
  const initialTitle = editing?.title ?? "";
  const initialNotes = editing?.notes ?? "";
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [organization, setOrganization] = useState(initialOrganization);
  const [title, setTitle] = useState(initialTitle);
  const [notes, setNotes] = useState(initialNotes);

  const dirty =
    displayName !== initialDisplayName ||
    organization !== initialOrganization ||
    title !== initialTitle ||
    notes !== initialNotes;
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={editing === null ? "New contact" : `Edit contact - ${editing.displayName}`}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          displayName: displayName.trim(),
          organization: cleanOptional(organization),
          title: cleanOptional(title),
          notes: cleanOptional(notes),
        });
      }}
    >
      <h3 className="minter__confirm-title">{editing === null ? "New contact" : `Edit contact - ${editing.displayName}`}</h3>
      <label className="tpl__field">
        Name
        <input type="text" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
      </label>
      <label className="tpl__field">
        Organization
        <input type="text" value={organization} onChange={(event) => setOrganization(event.target.value)} />
      </label>
      <div className="tpl__field-row">
        <label className="tpl__field">
          Title
          <input type="text" value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label className="tpl__field">
          Notes
          <input type="text" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <GlassDrawerFooter>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" form={formId} className="minter__confirm-go" disabled={busy}>
          {editing === null ? "Create contact" : "Save contact"}
        </button>
      </GlassDrawerFooter>
    </form>
  );
}

function MethodForm({
  contact,
  editing,
  busy,
  failure,
  onClose,
  onSubmit,
  onDirtyChange,
}: {
  contact: ClientContact;
  editing: ContactMethod | null;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onSubmit: (draft: ContactMethodDraft) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const initialMethodType = editing?.methodType ?? "mobile";
  const initialMethodValue = editing?.methodValue ?? "";
  const initialLabel = editing?.label ?? "";
  const initialPriority = String(editing?.priority ?? 1);
  const initialAlertCapable = editing?.alertCapable ?? true;
  const [methodType, setMethodType] = useState<ContactMethodType>(initialMethodType);
  const [methodValue, setMethodValue] = useState(initialMethodValue);
  const [label, setLabel] = useState(initialLabel);
  const [priority, setPriority] = useState(initialPriority);
  const [alertCapable, setAlertCapable] = useState(initialAlertCapable);

  const dirty =
    methodType !== initialMethodType ||
    methodValue !== initialMethodValue ||
    label !== initialLabel ||
    priority !== initialPriority ||
    alertCapable !== initialAlertCapable;
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={editing === null ? `Add method - ${contact.displayName}` : `Edit method - ${contact.displayName}`}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          methodType,
          methodValue: methodValue.trim(),
          label: cleanOptional(label),
          priority: Math.max(1, Number.parseInt(priority, 10) || 1),
          alertCapable,
        });
      }}
    >
      <h3 className="minter__confirm-title">
        {editing === null ? `Add method - ${contact.displayName}` : `Edit method - ${contact.displayName}`}
      </h3>
      <div className="tpl__field-row">
        <label className="tpl__field">
          Type
          <select value={methodType} onChange={(event) => setMethodType(event.target.value as ContactMethodType)}>
            {METHOD_OPTIONS.map((option) => (
              <option key={option} value={option}>{METHOD_LABELS[option]}</option>
            ))}
          </select>
        </label>
        <label className="tpl__field">
          Value
          <input type="text" value={methodValue} onChange={(event) => setMethodValue(event.target.value)} required />
        </label>
      </div>
      <div className="tpl__field-row">
        <label className="tpl__field">
          Label
          <input type="text" value={label} onChange={(event) => setLabel(event.target.value)} />
        </label>
        <label className="tpl__field">
          Priority
          <input type="number" min="1" max="999" value={priority} onChange={(event) => setPriority(event.target.value)} />
        </label>
      </div>
      <Switch
        checked={alertCapable}
        onChange={setAlertCapable}
        label="Alert capable"
        hint="This number or email can receive automated incident notifications."
      />
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <GlassDrawerFooter>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" form={formId} className="minter__confirm-go" disabled={busy}>
          Save method
        </button>
      </GlassDrawerFooter>
    </form>
  );
}

function AssignmentForm({
  contact,
  editing,
  clients,
  sites,
  clientNames,
  busy,
  failure,
  onClose,
  onSubmit,
  onDirtyChange,
}: {
  contact: ClientContact;
  editing: ContactAssignment | null;
  clients: ClientRow[];
  sites: ManagedSite[];
  clientNames: ReadonlyMap<string, string>;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onSubmit: (draft: ContactAssignmentDraft) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const initialTarget: ContactTargetValue = editing?.siteId !== null && editing?.siteId !== undefined
    ? `site:${editing.siteId}`
    : editing?.clientId !== null && editing?.clientId !== undefined
      ? `client:${editing.clientId}`
      : sites[0] !== undefined
        ? `site:${sites[0].siteId}`
        : clients[0] !== undefined
          ? `client:${clients[0].clientId}`
          : "";
  const initialRole = editing?.role ?? "primary";
  const initialPriority = String(editing?.priority ?? 1);
  const initialVisibleToGuards = editing?.visibleToGuards ?? true;
  const initialAlertRecipient = editing?.alertRecipient ?? true;
  const initialNotes = editing?.notes ?? "";
  const [target, setTarget] = useState<ContactTargetValue>(initialTarget);
  const [role, setRole] = useState<ContactAssignmentRole>(initialRole);
  const [priority, setPriority] = useState(initialPriority);
  const [visibleToGuards, setVisibleToGuards] = useState(initialVisibleToGuards);
  const [alertRecipient, setAlertRecipient] = useState(initialAlertRecipient);
  const [notes, setNotes] = useState(initialNotes);

  const dirty =
    target !== initialTarget ||
    role !== initialRole ||
    priority !== initialPriority ||
    visibleToGuards !== initialVisibleToGuards ||
    alertRecipient !== initialAlertRecipient ||
    notes !== initialNotes;
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={editing === null ? `Assign contact - ${contact.displayName}` : `Edit assignment - ${contact.displayName}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (target === "") {
          return;
        }
        const [kind, rawId] = target.split(":");
        const id = rawId ?? "";
        onSubmit({
          contactId: contact.contactId,
          clientId: kind === "client" ? id : null,
          siteId: kind === "site" ? id : null,
          role,
          priority: Math.max(1, Number.parseInt(priority, 10) || 1),
          visibleToGuards,
          alertRecipient,
          notes: cleanOptional(notes),
        });
      }}
    >
      <h3 className="minter__confirm-title">
        {editing === null ? `Assign contact - ${contact.displayName}` : `Edit assignment - ${contact.displayName}`}
      </h3>
      <p className="minter__confirm-note">
        Link this person to exactly one client or one site. New sites from step 1–2 show up here as
        soon as you save them. Primary contacts surface on guard devices; alert recipients get
        incident notifications.
      </p>
      <ContactTargetPicker
        clients={clients}
        sites={sites}
        clientNames={clientNames}
        value={target}
        onChange={setTarget}
        required
      />
      <label className="tpl__field">
        Role
        <select value={role} onChange={(event) => setRole(event.target.value as ContactAssignmentRole)}>
          {ROLE_OPTIONS.map((option) => (
            <option key={option} value={option}>{ROLE_LABELS[option]}</option>
          ))}
        </select>
      </label>
      <div className="tpl__field-row">
        <label className="tpl__field">
          Priority
          <input type="number" min="1" max="999" value={priority} onChange={(event) => setPriority(event.target.value)} />
        </label>
        <label className="tpl__field">
          Notes
          <input type="text" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
      </div>
      <div className="so-switch-stack">
        <Switch
          checked={visibleToGuards}
          onChange={setVisibleToGuards}
          label="Visible to guards"
          hint="Show this contact on guard mobile when they need a site phone number."
        />
        <Switch
          checked={alertRecipient}
          onChange={setAlertRecipient}
          label="Alert recipient"
          hint="Include in SMS or email when incidents fire for this client or site."
        />
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <GlassDrawerFooter>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" form={formId} className="minter__confirm-go" disabled={busy || target === ""}>
          Save assignment
        </button>
      </GlassDrawerFooter>
    </form>
  );
}

function ConfirmArchive({
  title,
  warning,
  busy,
  failure,
  onClose,
  onConfirm,
}: {
  title: string;
  warning: string;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="minter__confirm" aria-label={title}>
      <h3 className="minter__confirm-title">{title}</h3>
      <p className="minter__confirm-warning">{warning}</p>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="minter__confirm-go" onClick={onConfirm} disabled={busy}>
          Confirm archive
        </button>
      </div>
    </div>
  );
}
